/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  AnalyticsCallOptions,
  logEvent as firebaseLogEvent,
  getAnalytics,
  setCurrentScreen,
} from "firebase/analytics";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import ReactGA from "react-ga";

import { appHistory } from "config";
import { ANALYTICS_EVENTS } from "constants/analyticsKeys";
import { useFirebaseInterface } from "hooks/useFirebaseInterface";

import {
  AnalyticsContext,
  AnalyticsEvent,
  FirebaseEventParams,
} from "./AnalyticsContext";

interface IAnalyticsProviderProps {
  children?: ReactNode;
}

// ReactGA / Firebase tracking — enabled when REACT_APP_GA_TRACKING_ID is set
const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID;
const isGAEnabled = !!GA_TRACKING_ID;

// localStorage key written by our internal CookieConsentBar when the user accepts
const OUR_BANNER_CONSENT_STORAGE_KEY = "cookies-accepted";

// GTM script injected by our InitializeTagManager (requires REACT_APP_GTM_ID)
const hasInternalGtm = () => !!document.getElementById("gtm-script");

// Cookiebot CMP present in the page — manages consent externally via GTM Consent Mode
const hasCookiebotCmp = () => !!document.getElementById("Cookiebot");

// dataLayer consent is handled by an external system (Cookiebot or GTM hardcoded
// in HTML) rather than our internal CookieConsentBar.
// In these cases we push events unconditionally — the external CMP / GTM Consent Mode
// is responsible for holding or blocking tags until the user has accepted.
const isDataLayerConsentExternal = () => !hasInternalGtm() || hasCookiebotCmp();

export const AnalyticsProvider = ({ children }: IAnalyticsProviderProps) => {
  // True only when the user accepted via our internal CookieConsentBar.
  // This gates ReactGA and Firebase. GTM/dataLayer uses isDataLayerConsentExternal
  // instead so external CMPs are not bypassed.
  const [isOurBannerConsentGranted, setOurBannerConsentGranted] = useState(
    localStorage.getItem(OUR_BANNER_CONSENT_STORAGE_KEY) === "true",
  );

  const firebaseInterface = useFirebaseInterface();

  const firebaseAnalytics = useMemo(
    () => (firebaseInterface ? getAnalytics(firebaseInterface.app) : undefined),
    [firebaseInterface],
  );

  const trackFirebaseEvent = useCallback(
    (
      eventName: string,
      eventParams?: FirebaseEventParams | undefined,
      options?: AnalyticsCallOptions | undefined,
    ) => {
      if (!isOurBannerConsentGranted) {
        return;
      }
      if (firebaseAnalytics) {
        firebaseLogEvent(firebaseAnalytics, eventName, eventParams, options);
      }
    },
    [firebaseAnalytics, isOurBannerConsentGranted],
  );

  const trackGAEvent = useCallback(
    (category: string, action: string, label?: string) => {
      if (!isOurBannerConsentGranted) {
        return;
      }
      if (isGAEnabled) {
        ReactGA.event({
          category,
          action,
          label,
        });
      }
    },
    [isOurBannerConsentGranted],
  );

  const trackGTMEvent = useCallback(
    (payload: Record<string, unknown>) => {
      if (typeof window === "undefined") {
        return;
      }
      // When consent is managed externally (Cookiebot or GTM hardcoded in HTML),
      // push unconditionally — the external CMP handles consent gating.
      // When we manage consent ourselves, require the user to have accepted.
      if (!isOurBannerConsentGranted && !isDataLayerConsentExternal()) {
        return;
      }
      // Ensure dataLayer exists — InitializeTagManager only creates it when
      // REACT_APP_GTM_ID is set, but some clients configure GTM elsewhere
      // (hardcoded in HTML) or the script may not have loaded yet. Creating
      // the array here queues events so GTM can replay them on load.
      window.dataLayer = window.dataLayer || [];
      if ("ecommerce" in payload) {
        window.dataLayer.push({ ecommerce: null });
      }
      window.dataLayer.push(payload);
    },
    [isOurBannerConsentGranted],
  );

  // Unified facade — fans out a single event to Firebase + GA + GTM.
  // Use this by default in call sites; reach for the primitives only when
  // an event is genuinely provider-specific.
  const trackEvent = useCallback(
    (event: AnalyticsEvent) => {
      const { name, gaCategory, gaAction, gaLabel, params, ecommerce } = event;

      trackFirebaseEvent(name, params);

      trackGAEvent(gaCategory ?? "general", gaAction ?? name, gaLabel);

      const gtmPayload: Record<string, unknown> = {
        event: name,
        ...(params ?? {}),
      };
      if (ecommerce) {
        gtmPayload.ecommerce = ecommerce;
      }
      trackGTMEvent(gtmPayload);
    },
    [trackFirebaseEvent, trackGAEvent, trackGTMEvent],
  );

  const trackPageView = useCallback(() => {
    if (!isOurBannerConsentGranted) {
      return;
    }

    const page = window.location.pathname + window.location.search;

    if (isGAEnabled) {
      ReactGA.set({
        page,
      });

      ReactGA.pageview(page);
    }

    if (firebaseAnalytics) {
      setCurrentScreen(firebaseAnalytics, page);
      trackFirebaseEvent(ANALYTICS_EVENTS.SCREEN_VIEW);
    }
  }, [firebaseAnalytics, isOurBannerConsentGranted, trackFirebaseEvent]);

  const initGA = useCallback(() => {
    if (isGAEnabled && isOurBannerConsentGranted) {
      ReactGA.initialize(GA_TRACKING_ID as string, {
        debug: process.env.NODE_ENV === "development",
        titleCase: false,
      });

      appHistory.listen(() => {
        trackPageView();
      });
    }
  }, [isOurBannerConsentGranted, trackPageView]);

  const enableAnalytics = useCallback(() => {
    setOurBannerConsentGranted(true);

    // We need to init GA again here since cookies flag changed
    initGA();
    trackPageView();
  }, [initGA, trackPageView]);

  // Persist our banner consent to localStorage
  useEffect(() => {
    if (isOurBannerConsentGranted) {
      localStorage.setItem(OUR_BANNER_CONSENT_STORAGE_KEY, "true");
    }
  }, [isOurBannerConsentGranted]);

  // Init GA on load (when consent was already granted in a previous session)
  useEffect(() => {
    initGA();
  }, [initGA]);

  // Push page_view to dataLayer on every SPA route change.
  // GTM's built-in History Change trigger only works when configured in the
  // GTM container — pushing explicitly is the reliable approach for SPAs.
  useEffect(() => {
    return appHistory.listen((location) => {
      trackGTMEvent({
        event: ANALYTICS_EVENTS.PAGE_VIEW,
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    });
  }, [trackGTMEvent]);

  return (
    <AnalyticsContext.Provider
      value={{
        firebaseAnalytics,
        isCookiesAccepted: isOurBannerConsentGranted,
        enableAnalytics,
        trackEvent,
        trackFirebaseEvent,
        trackGAEvent,
        trackGTMEvent,
        trackPageView,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};
